import { shortStep } from 'views/sessions/deck';
import { ACTIONS, QUEUE_ACTIONS, REVIEW_TARGET_MINUTES, STEP_LABELS } from './constants';
import type {
	IChainStep,
	IMetricTile,
	IQueueRow,
	ISessionRow,
	IStatusSplit,
	IWatchFlow,
	NodeState,
	SessionVerdict,
} from './types';

const EMPTY_SPLIT: IStatusSplit = {
	completed: 0,
	processing: 0,
	pending: 0,
	rejected: 0,
	failed: 0,
};

const splitOf = (raw: any): IStatusSplit => ({
	completed: raw?.completed ?? 0,
	processing: raw?.processing ?? 0,
	pending: raw?.pending ?? 0,
	rejected: raw?.rejected ?? 0,
	failed: raw?.failed ?? 0,
});

export const sumSplit = (s: IStatusSplit) =>
	s.completed + s.processing + s.pending + s.rejected + s.failed;

/** viewBox is 0 0 100 26; higher value -> smaller y. */
export const sparkline = (series: number[]): string => {
	const pts = series.filter(n => typeof n === 'number');
	if (pts.length < 2) return '';
	const max = Math.max(...pts);
	const min = Math.min(...pts);
	// A series with no variation would draw a dead-flat line across the tile,
	// which reads as a real (and alarming) trend rather than as "no data".
	if (max === min) return '';
	const span = max - min || 1;
	const step = 100 / (pts.length - 1);
	return pts
		.map((v, i) => `${(i * step).toFixed(1)},${(23 - ((v - min) / span) * 20).toFixed(1)}`)
		.join(' ');
};

/** Second half of the range against the first, as a whole percent. */
export const trendDelta = (series: number[]): number | null => {
	const pts = series.filter(n => typeof n === 'number');
	if (pts.length < 4) return null;
	const mid = Math.floor(pts.length / 2);
	const first = pts.slice(0, mid).reduce((a, b) => a + b, 0);
	const second = pts.slice(mid).reduce((a, b) => a + b, 0);
	if (!first) return null;
	return Math.round(((second - first) / first) * 100);
};

/**
 * Metric strip. Totals come from /analytics (authoritative per-action status
 * counts); the sparkline and delta come from /onboarding-summary's daily series.
 */
export const toMetricTiles = (
	analytics: any,
	chartSeries: { name: string; data: number[] }[]
): IMetricTile[] =>
	ACTIONS.map(action => {
		const split = splitOf(analytics?.[action.key]);
		const series =
			chartSeries.find(s => s.name === action.series)?.data ?? [];
		return {
			key: action.key,
			label: action.label,
			total: sumSplit(split),
			split,
			tone: action.tone,
			delta: action.exact ? trendDelta(series) : null,
			points: sparkline(series),
			/**
			 * WHY THE SPARKLINE IS ABSENT, when it is — so the tile can say so
			 * instead of leaving a gap that reads as a chart that failed to draw.
			 *
			 * `sparkline()` returns '' for two very different reasons and the tile
			 * could not tell them apart: fewer than two points (the endpoint sent
			 * no series for this action) and a series with no variation (it sent
			 * one, and every day in it is the same — in practice all zeros). Five
			 * of the six tiles hit the second case on real data and rendered
			 * nothing at all beside a total of 220, which reads as broken rather
			 * than as "nothing happened this week".
			 */
			trendNote: action.exact ? '' : 'accreditation trend',
			seriesNote: !series.length
				? 'no daily data for this range'
				: series.every(v => v === series[0])
					? 'no activity in this range'
					: '',
		};
	});

/** Review queue: every action with work parked in `processing`. */
export const toReviewQueue = (analytics: any): IQueueRow[] => {
	const rows: IQueueRow[] = [];
	let total = 0;
	QUEUE_ACTIONS.forEach(({ key, label }) => {
		const n = analytics?.[key]?.processing ?? 0;
		total += n;
		if (n > 0) rows.push({ label, value: n, tone: 'a', indent: true });
	});
	return [{ label: 'Under review — total', value: total, tone: 'a' }, ...rows];
};

const nodeState = (node: any): NodeState => {
	const status = node?.action?.status;
	if (status === 'completed') return 'pass';
	if (status === 'rejected' || status === 'failed') return 'fail';
	if (status === 'processing') return 'review';
	if (node?.visited) return 'active';
	return 'unreached';
};

/**
 * Nodes arrive flat with parentId links. Walk the chain so the pills read in
 * flow order; anything unreachable from a root is appended in payload order.
 */
export const toChain = (nodes: any[] = []): IChainStep[] => {
	const byId = new Map<string, any>(nodes.map(n => [n._id, n]));
	const childrenOf = new Map<string, any[]>();
	const roots: any[] = [];
	nodes.forEach(n => {
		const parents = (n.parentId ?? []).filter((p: string) => byId.has(p));
		if (!parents.length) {
			roots.push(n);
			return;
		}
		const head = parents[0];
		childrenOf.set(head, [...(childrenOf.get(head) ?? []), n]);
	});

	const ordered: any[] = [];
	const seen = new Set<string>();
	const walk = (n: any) => {
		if (seen.has(n._id)) return;
		seen.add(n._id);
		ordered.push(n);
		(childrenOf.get(n._id) ?? []).forEach(walk);
	};
	roots.forEach(walk);
	nodes.forEach(n => !seen.has(n._id) && walk(n));

	return ordered.map(n => ({
		key: n._id,
		// the same short names the sessions and onboarding chains use — one map
		// (sessions/deck/derive) so the three lists cannot drift apart
		label: shortStep(STEP_LABELS[n.stepId] ?? n.stepId ?? n.key ?? 'step'),
		state: nodeState(n),
	}));
};

const VERDICTS: Record<string, { v: SessionVerdict; label: string }> = {
	completed: { v: 'ok', label: 'APPROVED' },
	approved: { v: 'ok', label: 'APPROVED' },
	rejected: { v: 'rej', label: 'REJECTED' },
	failed: { v: 'rej', label: 'FAILED' },
	processing: { v: 'rev', label: 'UNDER REVIEW' },
	pending: { v: 'strt', label: 'STARTED' },
};

const ageLabel = (minutes: number) => {
	if (minutes < 60) return `${minutes}m`;
	const h = Math.floor(minutes / 60);
	return `${h}h ${minutes % 60}m`;
};

export const toSessionRows = (
	sessions: any[] = [],
	now = Date.now(),
	limit = 10
): ISessionRow[] =>
	[...sessions]
		// most recently ACTIVE first — a session that moved a minute ago matters
		// more to an ops view than one merely created recently and untouched since
		.sort(
			(a, b) =>
				new Date(b.updatedAt ?? b.createdAt ?? 0).getTime() -
				new Date(a.updatedAt ?? a.createdAt ?? 0).getTime()
		)
		.slice(0, limit)
		.map(s => {
			const created = new Date(s.createdAt ?? 0);
			const chain = toChain(s.nodes);
			// A session whose chain has a node in review IS under review, whatever
			// the row-level status says.
			const inReview = chain.some(c => c.state === 'review');
			const base = VERDICTS[s.status] ?? { v: 'ns' as SessionVerdict, label: (s.status ?? 'unknown').toUpperCase() };
			const verdict = inReview && base.v === 'strt' ? { v: 'rev' as SessionVerdict, label: 'UNDER REVIEW' } : base;

			/**
			 * How long a compliance agent has been sitting on this — not how old
			 * the session is. The two are very different numbers: a session
			 * created three days ago that reached review ten minutes ago is not a
			 * ten-minute-old session, and it is not a three-day wait either.
			 *
			 * There is no per-node timestamp to read. A node carries only
			 * `createdAt` (when the step was created, not when it entered review)
			 * and `action` is `{actionId, status}` with no time at all. What is
			 * left is the session's `updatedAt`: for a session parked awaiting a
			 * decision, the last thing that happened to it IS entering review.
			 *
			 * CAVEAT, and it is a real one: anything else that touches the session
			 * afterwards — a comment, being marked viewed — moves `updatedAt` and
			 * makes the wait read shorter than it is. Reading the true moment
			 * needs the status activity in the activity log, which is a per-session
			 * fetch this list cannot afford. Under-reporting is the safer failure
			 * here: it never invents a breach that has not happened.
			 *
			 * A session nobody is waiting on has no such number, so it gets none.
			 */
			/**
			 * A flow that has moved in the last 24h is RUNNING, which is a
			 * different thing from a step awaiting a human. The step it is running
			 * is the last one it has reached — the deepest visited node in the
			 * chain — and it is only marked while nothing is actually blocked on a
			 * reviewer, since "waiting for a person" outranks "in motion".
			 */
			const activeMs = now - new Date(s.updatedAt ?? s.createdAt ?? 0).getTime();
			const recentlyActive = activeMs < 24 * 60 * 60 * 1000;
			const reached = (s.nodes ?? []).filter((n: any) => n?.visited);
			/* `pipelineNodeId`, not `_id`: the chart matches `data?.id`, which it
			   builds from `pipelineNodeId`. Returning `_id` compared a
			   session-node document id against a pipeline id and never matched —
			   so `running` has silently never rendered either. Same fix as
			   `stoppedNodeId` below. */
			const frontierNode = reached[reached.length - 1];
			const runningNodeId =
				recentlyActive && !inReview && reached.length
					? frontierNode?.pipelineNodeId ?? frontierNode?._id
					: undefined;

			/**
			 * STOPPED — where the applicant gave up.
			 *
			 * The exact inverse of `running`: the same deepest-visited node, but
			 * only when the session has NOT moved recently and is not finished or
			 * awaiting a reviewer. So a flow is either in motion, waiting on a
			 * person, done — or stalled, and only the last of those had no way to
			 * say so.
			 *
			 * This is why the state had to be derived rather than styled: the
			 * backend sends the node's colour as `pending`, which is true and
			 * useless, because every step downstream that nobody ever reached is
			 * `pending` too. `visited` is the signal that separates them.
			 *
			 * Terminal verdicts are excluded — a completed or rejected session did
			 * not stall, it finished.
			 */
			const terminal = verdict.v === 'ok' || verdict.v === 'rej';
			const stoppedNodeId =
				!recentlyActive && !inReview && !terminal && reached.length
					? frontierNode?.pipelineNodeId ?? frontierNode?._id
					: undefined;
			/* Whole days, floored: the chip says "17d", and rounding 17.9 up to 18
			   would overstate how long it has actually been sitting. */
			const stoppedForDays = stoppedNodeId
				? Math.max(1, Math.floor(activeMs / (24 * 60 * 60 * 1000)))
				: undefined;

			const waitMinutes = inReview || verdict.v === 'rev'
				? Math.max(
						0,
						Math.round((now - new Date(s.updatedAt ?? s.createdAt ?? 0).getTime()) / 60000)
				  )
				: null;
			return {
				id: s._id,
				name: s.name || s.email || 'Unnamed',
				email: s.email ?? '',
				// onBoardingName arrives as "Name — step+step+step"; the suffix is
				// the whole flow spelled out and blows the column width open,
				// pushing Status and Age off the panel entirely.
				flow: (s.fundName || (s.onBoardingName ?? '').split('—')[0] || '').trim(),
				time: created.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
				ageMinutes: waitMinutes ?? 0,
				// bare duration — the column heading says what it measures, so every
				// row repeating "waiting" was the same word 10 times down the page
				age: waitMinutes === null ? '' : ageLabel(waitMinutes),
				awaitingReview: waitMinutes !== null,
				runningNodeId,
				stoppedNodeId,
				stoppedForDays,
				lastActivity: new Date(s.updatedAt ?? s.createdAt ?? 0).getTime(),
				verdict: verdict.v,
				verdictLabel: verdict.label,
				chain,
				raw: s,
			};
		});

export const ageTone = (minutes: number) =>
	minutes > REVIEW_TARGET_MINUTES * 4
		? 'late'
		: minutes > REVIEW_TARGET_MINUTES
		? 'hot'
		: '';

/**
 * Flows on watch: onboarding flows that currently hold sessions in review,
 * heaviest first. The chain shown is the one from the oldest waiting session,
 * so the pill that is amber is the step actually holding the queue.
 */
export const toWatchFlows = (sessions: any[] = [], limit = 2): IWatchFlow[] => {
	const groups = new Map<string, { sessions: any[]; stuck: any[] }>();
	sessions.forEach(s => {
		// Same trim as the session rows: the "— step+step+step" suffix is the
		// flow spelled out, and it truncates the panel's heading to nothing.
		const name = (s.fundName || (s.onBoardingName ?? '').split('—')[0] || '').trim();
		if (!name) return;
		const g = groups.get(name) ?? { sessions: [], stuck: [] };
		g.sessions.push(s);
		if ((s.nodes ?? []).some((n: any) => n?.action?.status === 'processing')) {
			g.stuck.push(s);
		}
		groups.set(name, g);
	});

	return Array.from(groups.entries())
		.filter(([, g]) => g.stuck.length > 0)
		.sort((a, b) => b[1].stuck.length - a[1].stuck.length)
		.slice(0, limit)
		.map(([name, g]) => {
			const oldest = [...g.stuck].sort(
				(a, b) =>
					new Date(a.createdAt ?? 0).getTime() - new Date(b.createdAt ?? 0).getTime()
			)[0];
			const blocking = (oldest.nodes ?? []).find(
				(n: any) => n?.action?.status === 'processing'
			);
			const step = STEP_LABELS[blocking?.stepId] ?? blocking?.stepId ?? 'a step';
			return {
				name,
				sessions: g.sessions.length,
				chain: toChain(oldest.nodes),
				reason: `${g.stuck.length} of ${g.sessions.length} sessions are parked at ${step} awaiting human review.`,
			};
		});
};

export const emptySplit = EMPTY_SPLIT;
